import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  pdf,
  Image,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";

// ─── Imports from source files ────────────────────────────────────────────────
import {
  nutritionItems,
  mealLocationItems,
  feedingAssistanceItems,
  hydrationItems,
  assistiveDevicesItems,
  visionItems,
  speechItems,
  hearingItems,
  toileting as toiletingItems,
  brushingTeeth as brushingTeethItems,
  bedCheck as bedCheckItems,
  skinCare as skinCareItems,
  nutritionAndHydration as nutritionAndHydrationItems,
  undergarments as undergarmentItems,
  dressing as dressingItems,
  grooming as groomingItems,
  shower as showerItems,
  other as otherItems,
} from "../ServicePlanComponents/ServicePlanSteps/AssessmentStep";

import {
  safetyMonitoringItems,
  cognitiveActivitiesItems,
  coordinationOfCareItems,
  familyRepCommunicationItems,
  medicationAdministrationItems,
  cognitiveStatusItems,
  mobilityAmbulationTransferItems,
  nursingMedicalInstructionsItems,
  conditionsRequiringCaregiversItems,
} from "../ServicePlanComponents/ServicePlanSteps/CareAndSafetyStep";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Safely extract string from string | object */
const str = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object")
    return val.field || val.name || val.label || val.value || "";
  return String(val);
};

/** Flatten nested item tree → flat string[] */
const flattenItems = (items = []) => {
  const result = [];
  items.forEach((item) => {
    result.push(item.name);
    (item.subItems || []).forEach((sub) => result.push(sub.name));
  });
  return result;
};

// ─── PDF StyleSheet ───────────────────────────────────────────────────────────
const S = StyleSheet.create({
  page: {
    // add some extra bottom padding so content can't get clipped
    padding: 18,
    paddingBottom: 20,
    backgroundColor: "#F9FAFB",
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#262626",
  },
  title: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginBottom: 12,
    color: "#000",
  },
  sectionBar: {
    backgroundColor: "#E5E5E5",
    padding: "4 8",
    marginTop: 10,
    marginBottom: 6,
    textAlign: "center",
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#262626",
  },
  row2: { flexDirection: "row", gap: 6 },
  row3: { flexDirection: "row", gap: 6 },
  row4: { flexDirection: "row", gap: 5, marginTop: 6 },
  mt6: { marginTop: 6 },

  // Field
  fieldWrapper: { flex: 1 },
  fieldLabel: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#262626",
    marginBottom: 3,
  },
  fieldBox: {
    backgroundColor: "#FFFFFF",
    border: "1 solid #E5E5E5",
    borderRadius: 5,
    padding: "5 8",
    minHeight: 24,
  },
  fieldValue: { fontSize: 9, color: "#262626" },
  fieldPlaceholder: { fontSize: 9, color: "#9CA3AF" },

  // Textarea
  textarea: {
    backgroundColor: "#FFFFFF",
    border: "1 solid #E5E5E5",
    borderRadius: 5,
    padding: "5 8",
    minHeight: 50,
    marginTop: 6,
  },
  textareaLabel: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#262626",
    marginBottom: 3,
  },

  // Chips
  chipLabel: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#262626",
    marginBottom: 3,
    marginTop: 6,
  },
  chipBox: {
    backgroundColor: "#FFFFFF",
    border: "1 solid #E5E5E5",
    borderRadius: 5,
    padding: "5 8",
    minHeight: 36,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  chip: { backgroundColor: "#F2F4F7", borderRadius: 10, padding: "2 6" },
  chipText: { fontSize: 8, color: "#344054", fontFamily: "Helvetica-Bold" },

  // Accordion grids
  grid4: { flexDirection: "row", flexWrap: "wrap", gap: 5, marginTop: 4 },
  grid3: { flexDirection: "row", gap: 5, marginTop: 4 },
  accordionCol4: { width: "23.5%" },
  accordionCol3: { flex: 1 },
  accordionColStack: { flex: 1, flexDirection: "column", gap: 5 },

  // Accordion card — matches fieldsAccordion style
  accordionCard: {
    backgroundColor: "#FFFFFF",
    border: "1 solid #D9D9D9",
    borderRadius: 10,
    overflow: "hidden",
    marginBottom: 2,
  },
  accordionHeader: {
    backgroundColor: "#F4F3FF",
    borderBottom: "1 solid #D9D9D9",
    padding: "7 9",
  },
  accordionHeaderText: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#000",
  },
  accordionBody: { padding: "6 9", flexDirection: "column", gap: 3 },

  // Parent checkbox row
  accordionRow: { flexDirection: "row", alignItems: "center", marginBottom: 1 },

  // Checkbox square
  cbBox: {
    minWidth: 11,
    maxWidth: 11,
    minHeight: 11,
    maxHeight: 11,
    borderRadius: 3,
    marginRight: 4,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    backgroundColor: "#FFFFFF",
    flexShrink: 0,
  },
  cbBoxChecked: {
    minWidth: 11,
    maxWidth: 11,
    minHeight: 11,
    maxHeight: 11,
    borderRadius: 3,
    marginRight: 4,
    borderWidth: 1.5,
    borderColor: "#404040",
    backgroundColor: "#FAFAFA",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  checkmark: {
    width: "70%",
    height: "70%",
    objectFit: "contain",
  },

  // Label chip (checked) — #F2F4F7 pill matching checkboxAndLabelStyle
  itemChip: {
    backgroundColor: "#F2F4F7",
    borderRadius: 10,
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 6,
    paddingRight: 6,
  },
  itemChipText: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: "#525252",
  },
  // Label plain (unchecked)
  itemPlain: { fontSize: 7.5, color: "#525252" },

  // Sub-item row (indented like subCheckboxItem marginLeft 30)
  subRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 1,
    marginLeft: 14,
  },

  // Wide rows
  row2cols: { flexDirection: "row", gap: 5, marginTop: 5 },
  col2: { flex: 2 },
  col1: { flex: 1 },
  col5: { flex: 5 },
  col7: { flex: 7 },

  // Signature
  sigCard: {
    border: "2 solid #E5E7EB",
    borderRadius: 8,
    backgroundColor: "#FFF",
    padding: "14 14 10 14",
    marginBottom: 8,
    position: "relative",
    marginTop: 10,
  },
  sigBadge: {
    backgroundColor: "#FE9A00",
    borderRadius: 12,
    padding: "3 10",
    alignSelf: "flex-start",
    position: "absolute",
    top: -8,
    left: 10,
  },
  sigBadgeText: { color: "#FFF", fontSize: 8, fontFamily: "Helvetica-Bold" },
  sigRow: { flexDirection: "row", gap: 10, alignItems: "flex-end" },
  sigField: { flex: 1 },
  sigLabel: {
    fontSize: 7,
    color: "#6A7282",
    marginTop: 4,
    fontFamily: "Helvetica-Bold",
  },
  sigCardTitle: {
    fontSize: 7,
    color: "#4A5565",
    marginBottom: 4,
  },
  sigLine: {
    borderBottom: "2 solid #FFD230",
    minHeight: 22,
    padding: "2 2",
    fontSize: 9,
    color: "#262626",
  },
  sigImageBox: {
    borderBottom: "2 solid #FFD230",
    minHeight: 40,
    flex: 1.5,
    alignItems: "center",
    justifyContent: "center",
  },
  sigFieldValue: {
    fontSize: 9,
    color: "#262626",
  },
  sigImage: { maxHeight: 30, objectFit: "contain" },
  sigTextSign: { fontSize: 18, color: "#262626", fontFamily: "Island Moments" },
  textDisplayBox: {
    backgroundColor: "#FFFFFF",
    border: "1 solid #E5E5E5",
    borderRadius: 4,
    padding: "3 6",
    marginTop: 2,
    marginLeft: 14,
  },

  textDisplayText: {
    fontSize: 7.5,
    color: "#262626",
  },
});

// ─── PDF Sub-components ───────────────────────────────────────────────────────

const PDFField = ({ label, value, placeholder }) => (
  <View style={S.fieldWrapper}>
    <Text style={S.fieldLabel}>{label}</Text>
    <View style={S.fieldBox}>
      <Text style={str(value) ? S.fieldValue : S.fieldPlaceholder}>
        {str(value) || placeholder || "-"}
      </Text>
    </View>
  </View>
);

const PDFTextarea = ({ label, value }) => (
  <View>
    <Text style={S.textareaLabel}>{label}</Text>
    <View style={S.textarea}>
      <Text style={S.fieldValue}>{str(value) || "-"}</Text>
    </View>
  </View>
);

const PDFChipList = ({ label, items = [], textKey }) => (
  <View>
    <Text style={S.chipLabel}>{label}</Text>
    <View style={S.chipBox}>
      {items.length > 0 ? (
        items.map((item, i) => {
          const text = textKey ? (item?.[textKey] ?? str(item)) : str(item);
          return (
            <View key={i} style={S.chip}>
              <Text style={S.chipText}>{text}</Text>
            </View>
          );
        })
      ) : (
        <Text style={S.fieldPlaceholder}>-</Text>
      )}
    </View>
  </View>
);

/**
 * Mirrors ServicePlanAccordion exactly:
 * - Header: #F4F3FF bg, bold title
 * - Parent item: checkbox square + chip label (if checked) or plain label
 * - Sub-items: indented, same checkbox + chip/plain
 * - selected[] contains objects {name, subItems?:[{name}]} from ServicePlanAccordion
 */
const PDFAccordionCard = ({ title, itemsDef = [], selected = [], style }) => {
  // -----------------------------
  // Build lookup maps (optimized)
  // -----------------------------
  const selectedMap = {};
  const selectedParentNames = new Set();
  const selectedSubMap = {}; // parentName → Set of subItem names

  (selected || []).forEach((s) => {
    if (!s?.name) return;

    selectedMap[s.name] = s;
    selectedParentNames.add(s.name);

    if (s?.subItems?.length) {
      selectedSubMap[s.name] = new Set(s.subItems.map((sub) => sub.name));
    }
  });

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <View style={[S.accordionCard, style]}>
      {/* Header */}
      <View style={S.accordionHeader}>
        <Text style={S.accordionHeaderText}>{title}</Text>
      </View>

      {/* Body */}
      <View style={S.accordionBody}>
        {itemsDef.map((item, i) => {
          const isParentChecked = selectedParentNames.has(item.name);
          const hasSubItems = item.subItems && item.subItems.length > 0;

          const parentText = selectedMap[item.name]?.text;

          return (
            <View key={i}>
              {/* ---------------- Parent Row ---------------- */}
              <View style={S.accordionRow}>
                {/* Checkbox */}
                <View style={isParentChecked ? S.cbBoxChecked : S.cbBox}>
                  {isParentChecked && (
                    <Image
                      src="/assets/images/check-icon.png"
                      style={S.checkmark}
                      alt="Checkmark"
                    />
                  )}
                </View>

                {/* Label */}
                {isParentChecked ? (
                  <View style={S.itemChip}>
                    <Text style={S.itemChipText}>{item.name}</Text>
                  </View>
                ) : (
                  <Text style={S.itemPlain}>{item.name}</Text>
                )}
              </View>

              {/* Parent Text Display */}
              {isParentChecked && parentText && (
                <View style={S.textDisplayBox}>
                  <Text style={S.textDisplayText}>{parentText}</Text>
                </View>
              )}

              {/* ---------------- Sub Items ---------------- */}
              {isParentChecked && hasSubItems && (
                <View>
                  {item.subItems.map((sub, j) => {
                    const isSubChecked =
                      selectedSubMap[item.name]?.has(sub.name) || false;

                    const subText = selectedMap[item.name]?.subItems?.find(
                      (s) => s.name === sub.name,
                    )?.text;

                    return (
                      <View key={j}>
                        {/* Sub Row */}
                        <View style={S.subRow}>
                          <View style={isSubChecked ? S.cbBoxChecked : S.cbBox}>
                            {isSubChecked && (
                              <Image
                                src="/assets/images/check-icon.png"
                                style={S.checkmark}
                                alt="Checkmark"
                              />
                            )}
                          </View>

                          {isSubChecked ? (
                            <View style={S.itemChip}>
                              <Text style={S.itemChipText}>{sub.name}</Text>
                            </View>
                          ) : (
                            <Text style={S.itemPlain}>{sub.name}</Text>
                          )}
                        </View>

                        {/* Sub Text */}
                        {isSubChecked && subText && (
                          <View style={[S.textDisplayBox, { marginLeft: 28 }]}>
                            <Text style={S.textDisplayText}>{subText}</Text>
                          </View>
                        )}
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const PDFSignatureRow = ({ sig, index }) => (
  <View style={S.sigCard}>
    <Text style={S.sigCardTitle}>{sig?.position || ""}</Text>
    <View style={S.sigRow}>
      <View style={S.sigField}>
        <View style={S.sigLine}>
          <Text style={S.sigFieldValue}>{sig?.fullName || ""}</Text>
        </View>
        <Text style={S.sigLabel}>Full Name</Text>
      </View>
      <View style={S.sigField}>
        <View style={S.sigLine}>
          <Text style={S.sigFieldValue}>{sig?.title || ""}</Text>
        </View>
        <Text style={S.sigLabel}>Title</Text>
      </View>
      <View style={[S.sigField, { flex: 1.5 }]}>
        <View style={S.sigImageBox}>
          {sig?.signature ? (
            <Image src={sig.signature} style={S.sigImage} />
          ) : sig?.signatureImage ? (
            <Image src={sig.signatureImage} style={S.sigImage} />
          ) : sig?.signatureText ? (
            <Text style={S.sigTextSign}>{sig.signatureText}</Text>
          ) : (
            <Text style={S.fieldPlaceholder}>—</Text>
          )}
        </View>
        <Text style={S.sigLabel}>Signature</Text>
      </View>
      <View style={S.sigField}>
        <View style={S.sigLine}>
          <Text style={S.sigFieldValue}>
            {sig?.date ? moment(sig.date).format("MM/DD/YYYY") : ""}
          </Text>
        </View>
        <Text style={S.sigLabel}>Date</Text>
      </View>
    </View>
  </View>
);

// ─── Main PDF Document ────────────────────────────────────────────────────────
export const ServicePlanDocument = ({ planPayload, taskTypeDetails, businessName }) => {
  const data = planPayload?.servicePlanData || {};
  const clientData = data?.clientData || {};
  const assessment = data?.assessment || {};
  const adl = data?.activitiesOfDailyLiving || {};
  const careAndSafety = data?.careAndSafety || {};
  const signature = data?.signature || [];

  const dateRange =
    planPayload?.startDate && planPayload?.endDate
      ? `${moment(planPayload.startDate).format("MM/DD/YYYY")} - ${moment(planPayload.endDate).format("MM/DD/YYYY")}`
      : "-";

  const buildAdlSelected = (fieldsDone = [], subTask) => {
    if (!fieldsDone.length || !subTask?.fields?.length) return [];

    return subTask.fields
      .filter((field) => fieldsDone.some((done) => done.field === field._id))
      .map((field) => {
        const doneItem = fieldsDone.find((done) => done.field === field._id);

        return {
          name: field.name,
          text: doneItem?.textField || "",
        };
      });
  };

  const buildAdlItemsDef = (subTask) => {
    if (!subTask?.fields) return [];

    return subTask.fields.map((field) => ({
      name: field?.name,
    }));
  };

  const adlItemsDef = buildAdlItemsDef(taskTypeDetails);
  const adlSelected = buildAdlSelected(adl?.fieldsDone, taskTypeDetails);

  Font.register({
    family: "Helvetica",
    fonts: [
      {
        src: "Helvetica",
      },
    ],
  });

  Font.register({
    family: "Island Moments",
    fonts: [
      {
        src: "/assets/fonts/IslandMoments-Regular.ttf",
      },
    ],
  });

  return (
    <Document>
      {/* ══════ PAGE 1 – Intake Data ══════ */}
      <Page size="A4" style={S.page} wrap>
        <Text style={S.title}>{businessName}</Text>

        <View style={S.row3}>
          <PDFField label="Facility" value={planPayload?.facility} />
          <PDFField label="Date" value={dateRange} />
          <PDFField
            label="Assessment Type"
            value={clientData?.assessmentType}
          />
        </View>

        <Text style={S.sectionBar}>Intake Data</Text>
        <View style={S.row3}>
          <PDFField label="First Name *" value={clientData?.firstName} />
          <PDFField label="Last Name *" value={clientData?.lastName} />
          <PDFField
            label="Admission Date"
            value={
              clientData?.admissionDate
                ? moment(clientData.admissionDate).format("MM/DD/YYYY")
                : null
            }
          />
        </View>
        <View style={S.row4}>
          <PDFField
            label="Date of Birth *"
            value={
              clientData?.dateOfBirth
                ? moment(clientData.dateOfBirth).format("MM/DD/YYYY")
                : null
            }
          />
          <PDFField label="Code Status *" value={clientData?.codeStatus} />
          <PDFField label="Level of Care *" value={clientData?.levelOfCare} />
          <PDFField
            label="Date of last TB Screening *"
            value={
              clientData?.dateOfLastTBScreening
                ? moment(clientData.dateOfLastTBScreening).format("MM/DD/YYYY")
                : null
            }
          />
        </View>
        <View style={S.row4}>
          <PDFField label="Weight (lbs)" value={clientData?.weightLbs} />
          <PDFField label="Blood Pressure" value={clientData?.bloodPressure} />
          <PDFField label="Pulse" value={clientData?.pulse} />
          <PDFField label="Oxygen" value={clientData?.oxygen} />
        </View>
        <View style={S.row4}>
          <PDFField label="Respiration" value={clientData?.respiration} />
          <PDFField label="Temperature" value={clientData?.temperature} />
          <PDFField label="Glucose Check" value={clientData?.glucoseCheck} />
          <PDFField label="Frequency" value={clientData?.frequency} />
        </View>

        <PDFChipList
          label="Diagnosis"
          items={clientData?.diagnosis}
          textKey="name"
        />
        <PDFChipList label="Allergies" items={clientData?.allergies} />
        <PDFTextarea
          label="Other/Past History"
          value={clientData?.otherHistory}
        />

        <Text style={S.sectionBar}>Emergency Contact</Text>
        <View style={S.row3}>
          <PDFField label="POA Name" value={clientData?.poaName} />
          <PDFField label="Relationship" value={clientData?.relationship} />
          <PDFField label="Phone Number" value={clientData?.poaPhone} />
        </View>
        <View style={[S.row3, S.mt6]}>
          <PDFField
            label="Primary Care Physician"
            value={clientData?.primaryCarePhysician}
          />
          <PDFField label="Practice Name" value={clientData?.practiceName} />
          <PDFField label="Phone Number" value={clientData?.pcpPhone} />
        </View>
        <View style={[S.row2, S.mt6]}>
          <PDFField label="Pharmacy Name" value={clientData?.pharmacyName} />
          <PDFField label="Phone Number" value={clientData?.pharmacyPhone} />
        </View>
        <View style={[S.row3, S.mt6]}>
          <PDFField
            label="Home Health Agency"
            value={clientData?.homeHealthAgency}
          />
          <PDFField
            label="Services Received"
            value={clientData?.servicesReceived}
          />
          <PDFField
            label="Phone Number"
            value={clientData?.homeHealthAgencyPhone}
          />
        </View>
        <View style={[S.row2, S.mt6]}>
          <PDFField label="Hospice Agency" value={clientData?.hospiceAgency} />
          <PDFField label="Phone Number" value={clientData?.hospicePhone} />
        </View>
        <PDFTextarea label="Others" value={clientData?.others} />
        <Text style={S.sectionBar}>Physical Assessment</Text>
        <View style={S.grid4}>
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Nutrition"
            itemsDef={nutritionItems}
            selected={assessment?.nutrition}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Meals Location"
            itemsDef={mealLocationItems}
            selected={assessment?.mealsLocation}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Feeding Assistance"
            itemsDef={feedingAssistanceItems}
            selected={assessment?.feedingAssistance}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Hydration"
            itemsDef={hydrationItems}
            selected={assessment?.hydration}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Assistive Devices"
            itemsDef={assistiveDevicesItems}
            selected={assessment?.assistiveDevices}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Vision"
            itemsDef={visionItems}
            selected={assessment?.vision}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Speech"
            itemsDef={speechItems}
            selected={assessment?.speech}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Hearing"
            itemsDef={hearingItems}
            selected={assessment?.hearing}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Toileting"
            itemsDef={toiletingItems}
            selected={assessment?.toileting}
          />
        </View>
        <PDFTextarea
          label="Notes"
          value={assessment?.physicalAssessmentNotes}
        />

        <Text style={S.sectionBar}>Occupational Assessment</Text>
        <View style={S.grid4}>
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Brushing Teeth"
            itemsDef={brushingTeethItems}
            selected={assessment?.brushingTeeth}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Bed Check"
            itemsDef={bedCheckItems}
            selected={assessment?.bedCheck}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Skin Care"
            itemsDef={skinCareItems}
            selected={assessment?.skinCare}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Nutrition and Hydration"
            itemsDef={nutritionAndHydrationItems}
            selected={assessment?.nutritionAndHydration}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Undergarments"
            itemsDef={undergarmentItems}
            selected={assessment?.undergarments}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Dressing"
            itemsDef={dressingItems}
            selected={assessment?.dressing}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Grooming"
            itemsDef={groomingItems}
            selected={assessment?.grooming}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Shower"
            itemsDef={showerItems}
            selected={assessment?.shower}
          />
          <PDFAccordionCard
            style={S.accordionCol4}
            title="Other"
            itemsDef={otherItems}
            selected={assessment?.other}
          />
        </View>
        <PDFTextarea
          label="Notes"
          value={assessment?.occupationalAssessmentNotes}
        />

        <Text style={S.sectionBar}>Activities of Daily Living</Text>

        <View style={S.grid4}>
          {taskTypeDetails?.subTasks?.map((subTask, index) => {
            const itemsDef = buildAdlItemsDef(subTask);
            const selected = buildAdlSelected(adl?.fieldsDone, subTask);

            return (
              <PDFAccordionCard
                key={subTask?._id || index}
                style={S.accordionCol4}
                title={subTask?.task?.name}
                itemsDef={itemsDef}
                selected={selected}
              />
            );
          })}
        </View>
        <Text style={S.sectionBar}>Care and Safety</Text>

        <View style={S.grid3}>
          <PDFAccordionCard
            style={S.accordionCol3}
            title="Safety Monitoring"
            itemsDef={safetyMonitoringItems}
            selected={careAndSafety?.safetyMonitoring}
          />
          <PDFAccordionCard
            style={S.accordionCol3}
            title="Cognitive Activities"
            itemsDef={cognitiveActivitiesItems}
            selected={careAndSafety?.cognitiveActivities}
          />
          <View style={S.accordionColStack}>
            <PDFAccordionCard
              title="Coordination of Care"
              itemsDef={coordinationOfCareItems}
              selected={careAndSafety?.coordinationOfCare}
            />
            <PDFAccordionCard
              title="Family/Rep Communication"
              itemsDef={familyRepCommunicationItems}
              selected={careAndSafety?.familyRepCommunication}
            />
          </View>
        </View>

        <View style={S.row2cols}>
          <View style={S.col2}>
            <PDFAccordionCard
              title="Medication Administration and Treatment"
              itemsDef={medicationAdministrationItems}
              selected={careAndSafety?.medicationAdministration}
            />
          </View>
          <View style={S.col1}>
            <PDFAccordionCard
              title="Cognitive Status"
              itemsDef={cognitiveStatusItems}
              selected={careAndSafety?.cognitiveStatus}
            />
          </View>
        </View>

        <View style={S.row2cols}>
          <View style={S.col5}>
            <PDFAccordionCard
              title="Mobility Ambulation Transfer"
              itemsDef={mobilityAmbulationTransferItems}
              selected={careAndSafety?.mobilityAmbulationTransfer}
            />
          </View>
          <View style={S.col7}>
            <PDFAccordionCard
              title="Nursing/Medical Instructions"
              itemsDef={nursingMedicalInstructionsItems}
              selected={careAndSafety?.nursingMedicalInstructions}
            />
          </View>
        </View>

        <View style={S.row2cols}>
          <View style={S.col2}>
            <PDFAccordionCard
              title="Conditions requiring caregivers to call Physician"
              itemsDef={conditionsRequiringCaregiversItems}
              selected={careAndSafety?.conditionsRequiringCaregivers}
            />
          </View>
          <View style={S.col1} />
        </View>

      </Page>

      {signature.length > 0 && (
        <Page style={S.page}>
          <Text style={S.title}>Signatures</Text>
          <View style={{ marginTop: 8 }}>
            {/* {signature.map((sig, i) => (
              <PDFSignatureRow key={sig?.id || i} sig={sig} index={i} />
            ))} */}
            {signature?.filter((sig) => sig?.signatureRequestStatus === "success")?.map((sig, i) => (
              <PDFSignatureRow key={sig?.id || i} sig={sig} index={i} />
            ))}
          </View>
        </Page>
      )}
    </Document>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT 1: Inline PDFViewer — use in SignatureStep's Document Preview panel
// ─────────────────────────────────────────────────────────────────────────────
export const ServicePlanPDFPreview = (props) => {
  return <PDFViewer style={{ width: "100%", height: 620, border: "none" }}>
    <ServicePlanDocument {...props} />
  </PDFViewer>;
};

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT 2: getServicePlanBlob — get Blob for S3 upload / email attachment
//
// Usage (in ServicePlanForm or SignatureStep):
//   const blob = await getServicePlanBlob(planPayload);
//   const file = new File([blob], "service-plan.pdf", { type: "application/pdf" });
//   const result = await uploadFileToS3Api({ file });
//   // result.uploadURL  ← use as fileUrl for email attachment
// ─────────────────────────────────────────────────────────────────────────────
export const getServicePlanBlob = async (props) => {
  const blob = await pdf(<ServicePlanDocument {...props} />).toBlob();
  return blob;
};

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT 3: downloadServicePlanPDF — triggers browser download
//
// Usage:
//   await downloadServicePlanPDF(planPayload, "Service_Plan_John.pdf");
// ─────────────────────────────────────────────────────────────────────────────
export const downloadServicePlanPDF = async (
  planPayload,
  taskTypeDetails,
  fileName = "service-plan.pdf",
) => {
  const blob = await getServicePlanBlob(planPayload, taskTypeDetails);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

export default ServicePlanDocument;
